// src/components/HashReferenceAutocomplete.tsx
import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useLayoutEffect,
} from 'react';
import {
    Box,
    ClickAwayListener,
    List,
    ListItemButton,
    Paper,
    Popper,
    Typography,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';
import {
    listRecords,
    type RecordListOut,
    type RecordListItem,
} from '../api/records';

type MinimalRecord = { triplet_id: string; title: string; created_at: string };

// ---- Module-scope cache: load once per session ----
let ALL_RECORDS_CACHE: MinimalRecord[] | null = null;
let LOAD_PROMISE: Promise<MinimalRecord[]> | null = null;
export function invalidateHashReferenceCache() {
    ALL_RECORDS_CACHE = null;
    LOAD_PROMISE = null;
}

async function loadAllRecordsOnce(): Promise<MinimalRecord[]> {
    if (ALL_RECORDS_CACHE) return ALL_RECORDS_CACHE;
    if (LOAD_PROMISE) return LOAD_PROMISE;

    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    const fetchPage = async (
        page: number,
        perPage: number
    ): Promise<RecordListOut> => {
        try {
            return await listRecords({ page, per_page: perPage });
        } catch {
            await sleep(300);
            return await listRecords({ page, per_page: perPage });
        }
    };

    LOAD_PROMISE = (async () => {
        const perPage = 100;
        let page = 1;
        const all: MinimalRecord[] = [];

        while (true) {
            const res = await fetchPage(page, perPage);
            const items = (res.items ?? []) as RecordListItem[];
            for (const it of items) {
                all.push({
                    triplet_id: it.triplet_id,
                    title: it.title,
                    created_at: it.created_at,
                });
            }
            if (items.length < perPage) break;
            page += 1;
        }

        // newest → oldest
        all.sort(
            (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
        );

        ALL_RECORDS_CACHE = all;
        LOAD_PROMISE = null;
        return all;
    })();

    return LOAD_PROMISE;
}

type Props = {
    value: string;
    onChange: (next: string) => void;
    /** Pass your <TextField ... /> here */
    textField: React.ReactElement<TextFieldProps>;
    maxSuggestions?: number;
};

// Allowed while linking: letters/numbers, spaces, dashes (ASCII/en/em), and parentheses.
const TOKEN_CHARS = /^[a-z0-9 ()\-–—]*$/i;
const TOKEN_CHAR_RE = /[a-z0-9 ()\-–—]/i;

// Convert backend ISO (UTC) → local Date. If no TZ suffix, append 'Z'.
function localDateFromUtc(iso: string): Date {
    const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(iso);
    const stamp = hasTZ ? iso : iso + 'Z';
    return new Date(stamp);
}

// Compact, readable local timestamp. Adds year if not current year.
function formatStamp(iso: string) {
    try {
        const d = localDateFromUtc(iso);
        const now = new Date();
        const opts: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };
        if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
        return d.toLocaleString(undefined, opts);
    } catch {
        return iso;
    }
}

const HashReferenceAutocomplete: React.FC<Props> = ({
    value,
    onChange,
    textField,
    maxSuggestions = 5,
}) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
        null
    );

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);

    const [tokenStart, setTokenStart] = useState<number | null>(null);
    const [query, setQuery] = useState('');

    const [loadingAll, setLoadingAll] = useState(false);
    const [allRecords, setAllRecords] = useState<MinimalRecord[]>([]);

    const desiredCaretPosRef = useRef<number | null>(null);

    useEffect(() => {
        let mounted = true;
        setLoadingAll(true);
        loadAllRecordsOnce()
            .then((recs) => {
                if (mounted) setAllRecords(recs);
            })
            .catch((err) => {
                console.error('Failed to load records for # autocomplete', err);
            })
            .finally(() => {
                if (mounted) setLoadingAll(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Precompute lowercase helpers for searching.
    const searchables = useMemo(
        () =>
            allRecords.map((r) => {
                const title_lc = (r.title ?? '').toLowerCase();
                const id_lc = r.triplet_id.toLowerCase();
                const triplet_tokens = id_lc.split('-').filter(Boolean);
                return { ...r, title_lc, triplet_tokens };
            }),
        [allRecords]
    );

    // Hyphenated-term matcher: contiguous sliding window, prefix-friendly.
    function hyphenatedMatches(tokens: string[], rawTerm: string): boolean {
        const term = rawTerm.replace(/[–—]/g, '-'); // normalize dashes
        const endsWithHyphen = term.endsWith('-');
        const baseParts = term.split('-').filter(Boolean);
        if (!baseParts.length) return true; // keep suggestions on just '-'

        const windowLen = endsWithHyphen
            ? baseParts.length + 1
            : baseParts.length;
        for (let i = 0; i + windowLen <= tokens.length; i++) {
            const candidate = tokens.slice(i, i + windowLen).join('-');
            if (candidate.startsWith(term)) return true;
            if (endsWithHyphen) {
                const basePrefix = baseParts.join('-') + '-';
                if (candidate.startsWith(basePrefix)) return true;
            }
        }
        return false;
    }

    // Keep global created_at order; filter with AND semantics.
    const options = useMemo(() => {
        if (!searchables.length) return [];

        // Normalize Unicode dashes to '-' for matching
        const q = query.toLowerCase().replace(/[–—]/g, '-');

        if (!q.trim()) {
            return searchables.slice(0, maxSuggestions);
        }

        const terms = q.split(/\s+/).filter(Boolean);

        const matches = searchables.filter((rec) =>
            terms.every((term) => {
                if (term.includes('-')) {
                    return hyphenatedMatches(rec.triplet_tokens, term);
                }
                // non-hyphen → title substring OR any triplet token substring
                if ((rec.title_lc ?? '').includes(term)) return true;
                return rec.triplet_tokens.some((tok) => tok.includes(term));
            })
        );

        return matches.slice(0, maxSuggestions);
    }, [searchables, query, maxSuggestions]);

    useEffect(() => {
        if (!open) setHighlight(0);
    }, [open]);

    useLayoutEffect(() => {
        if (desiredCaretPosRef.current != null && inputRef.current) {
            const pos = desiredCaretPosRef.current;
            try {
                inputRef.current.setSelectionRange(pos, pos);
                inputRef.current.focus();
            } catch {
                // no-op across input/textarea
            }
            desiredCaretPosRef.current = null;
        }
    }, [value]);

    // Clone the given TextField and attach handlers + inputRef
    const tf = React.cloneElement(textField, {
        inputRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => {
            inputRef.current = el;
            const prev =
                (textField.props.inputRef as
                    | ((
                          el: HTMLInputElement | HTMLTextAreaElement | null
                      ) => void)
                    | undefined) ?? undefined;
            if (typeof prev === 'function') prev(el);
        },
        onChange: (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            const caret = e.target.selectionStart ?? value.length;
            const nextVal = e.target.value;
            onChange(nextVal);

            const left = nextVal.slice(0, caret);
            const hashIdx = left.lastIndexOf('#');

            // start-of-line OR preceded by whitespace/(/[/{
            const prev = left[hashIdx - 1] ?? '';
            const boundaryOK =
                hashIdx === 0 ||
                prev === ' ' ||
                prev === '\t' ||
                prev === '\n' ||
                prev === '(' ||
                prev === '[' ||
                prev === '{';

            if (hashIdx >= 0 && boundaryOK) {
                const after = left.slice(hashIdx + 1);

                // Immediate space after '#' exits linking mode.
                if (after.length > 0 && after[0] === ' ') {
                    setOpen(false);
                    setTokenStart(null);
                    setQuery('');
                    return;
                }

                // Allow letters/numbers/space/dash/parentheses while linking
                if (TOKEN_CHARS.test(after)) {
                    setTokenStart(hashIdx);
                    setQuery(after.toLowerCase());
                    setOpen(true);
                    if (inputRef.current) {
                        setAnchorEl(inputRef.current as unknown as HTMLElement);
                    }
                    return;
                }
            }

            setOpen(false);
            setTokenStart(null);
            setQuery('');
        },
        onKeyDown: (e: React.KeyboardEvent) => {
            if (!open) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) =>
                    Math.min(h + 1, Math.max(options.length - 1, 0))
                );
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                if (options.length) {
                    e.preventDefault();
                    accept(options[highlight].triplet_id);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
                return;
            }
            // Backspace is default browser behavior now — no custom handler needed.
        },
    });

    function getActiveToken() {
        if (tokenStart == null || !inputRef.current) return null;
        const caret =
            (inputRef.current as HTMLInputElement | HTMLTextAreaElement)
                .selectionStart ?? value.length;

        let end = tokenStart + 1;
        // include letters/numbers/spaces/dashes/parentheses as part of the token
        while (end < value.length && TOKEN_CHAR_RE.test(value[end]!)) end++;

        if (caret < tokenStart + 1) return null;
        return { start: tokenStart, end };
    }

    function accept(tripletId: string) {
        const token = getActiveToken();
        if (!token) return;

        const before = value.slice(0, token.start + 1); // includes '#'
        const after = value.slice(token.end);
        const inserted = tripletId;
        const next = `${before}${inserted} ${after}`; // add a trailing space once

        // caret goes right after the inserted id + trailing space
        desiredCaretPosRef.current = token.start + 1 + inserted.length + 1;

        onChange(next);
        setOpen(false);
        setTokenStart(null);
        setQuery('');
    }

    return (
        <>
            {tf}
            <Popper
                // only show if open AND (loading OR we have options)
                open={open && (!!options.length || loadingAll)}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
            >
                <Paper elevation={4}>
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <List dense sx={{ minWidth: 380, maxWidth: 560 }}>
                            {loadingAll && (
                                <ListItemButton disabled>
                                    <Typography>Loading records…</Typography>
                                </ListItemButton>
                            )}
                            {!loadingAll &&
                                options.map((opt, i) => (
                                    <ListItemButton
                                        key={opt.triplet_id}
                                        selected={i === highlight}
                                        onMouseEnter={() => setHighlight(i)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => accept(opt.triplet_id)}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flex: '1 1 auto',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography fontWeight={600}>
                                                    #{opt.triplet_id}
                                                </Typography>
                                                {opt.title && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {opt.title}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    ml: 2,
                                                }}
                                                title={localDateFromUtc(
                                                    opt.created_at
                                                ).toLocaleString()}
                                            >
                                                {formatStamp(opt.created_at)}
                                            </Typography>
                                        </Box>
                                    </ListItemButton>
                                ))}
                        </List>
                    </ClickAwayListener>
                </Paper>
            </Popper>
        </>
    );
};

export default HashReferenceAutocomplete;
